import {useEffect, useRef, useState} from 'react';
import {radarApi, type ResearchSettings, type ResearchState} from './radarApi';
import {RadarDialog} from './RadarDialog';
import {useRadarLocale} from '@/i18n/RadarLocaleContext';
import {RadarListCampaign} from './RadarListCampaign';
import '@/styles/radar-outreach.css';

const nodes = [['company_research','Company research & fit'],['contacts','Contact research'],['draft','Email draft']] as const;
const verdicts = {fit:'Matched',not_fit:'Not matched',insufficient_information:'More evidence needed'};
const csvTemplate='company_name,company_website,source_name,source_edition,source_url,source_company_id,country_region,product_summary,booth_no,collected_at\r\n';

export function RadarResearchSettings({token,workspaceId,onChanged}:{
  token:string;workspaceId:string;onChanged?:()=>void;
}) {
  const {t}=useRadarLocale();
  const [open,setOpen]=useState(false),[data,setData]=useState<ResearchState|null>(null);
  const [settings,setSettings]=useState<ResearchSettings|null>(null);
  const [busy,setBusy]=useState(false),[error,setError]=useState(''),[notice,setNotice]=useState('');
  const [campaignOpen,setCampaignOpen]=useState(false);
  const current=useRef(true),lock=useRef(false);
  useEffect(()=>{current.current=true;return()=>{current.current=false;};},[token,workspaceId]);
  useEffect(()=>{
    if(!open)return;
    let active=true;
    radarApi.researchSettings(token,workspaceId).then(next=>{
      if(active){setData(next);setSettings(next.settings);}
    }).catch(e=>{if(active)setError(e instanceof Error?e.message:'Could not load research settings.');});
    return()=>{active=false;};
  },[open,token,workspaceId]);
  const dirty=!!settings && JSON.stringify(settings)!==JSON.stringify(data?.settings);
  const selected=data?.lists?.find(list=>list.id===settings?.active_list_id);
  async function perform(action:()=>Promise<void>) {
    if(lock.current)return;
    lock.current=true;setBusy(true);setError('');setNotice('');
    try{await action();}catch(e){if(current.current)setError(e instanceof Error?e.message:'Please try again.');}
    finally{lock.current=false;if(current.current)setBusy(false);}
  }
  async function save() {
    if(!settings)return;
    await perform(async()=>{
      const next=await radarApi.saveResearchSettings(token,workspaceId,settings);
      if(current.current){setData(next);setSettings(next.settings);setNotice('Saved. The next unfinished step uses these choices.');onChanged?.();}
    });
  }
  async function upload(file:File) {
    await perform(async()=>{
      if(file.size>2_000_000)throw new Error('Import a UTF-8 CSV of up to 2 MB and 2,000 rows.');
      const imported=await radarApi.importResearchList(token,workspaceId,file.name,await file.text());
      const next=await radarApi.researchSettings(token,workspaceId);
      if(current.current){
        setData(next);setSettings(old=>({... (old||next.settings),source_mode:'seed_list',active_list_id:imported.id}));
        setNotice(`Imported ${imported.company_count} companies. Save settings to use this list.`);
      }
    });
  }
  return <>
    <button className="radar-text-button" onClick={()=>setOpen(true)}>{t('Research settings')}</button>
    <RadarDialog open={open} title={t(data?.internal?'Internal research':'Research settings')} closeLabel={t('Close research settings')} onClose={()=>{if(!busy)setOpen(false);}}>
      {!data ? <p role="status">{t('Loading research settings…')}</p> : !data.available ? <p>{t('Research settings are unavailable.')}</p> : settings && <>
        <p>{t('Use your confirmed Market Brief to assess each company. Importing a list does not change your target or start outreach.')}</p>
        <label className="radar-field"><span>{t('Company source')}</span>
          <select disabled={busy} value={settings.source_mode} onChange={e=>setSettings({...settings,source_mode:e.target.value as ResearchSettings['source_mode']})}>
            <option value="open_search">{t('Open search')}</option><option value="seed_list">{t('Imported company list')}</option>
          </select>
        </label>
        <label className="radar-field"><span>{t('Import company CSV')}</span>
          <input type="file" accept=".csv,text/csv" disabled={busy} onChange={e=>{const file=e.target.files?.[0];if(file)void upload(file);e.target.value='';}}/>
        </label>
        <p><a className="radar-text-button" download="buyer-radar-company-list.csv" href={'data:text/csv;charset=utf-8,'+encodeURIComponent(csvTemplate)}>{t('Download CSV template')}</a></p>
        <p className="radar-muted">{t('Use a UTF-8 CSV, up to 2 MB and 2,000 companies. Include company_name, source_name, source_edition and a public source_url for each row.')}</p>
        {settings.source_mode==='seed_list' && <>
          <label className="radar-field"><span>{t('Active company list')}</span>
            <select disabled={busy} value={settings.active_list_id||''} onChange={e=>setSettings({...settings,active_list_id:e.target.value||null})}>
              <option value="">{t('Choose a list')}</option>
              {data.lists?.map(list=><option key={list.id} value={list.id}>{list.name}</option>)}
            </select>
          </label>
          {selected && <p>{t('{processed} / {total} companies processed',{processed:selected.processed_count,total:selected.company_count})}</p>}
          <p className="radar-muted">{t('Manual and daily runs share progress. Each round researches up to five companies. When the list ends, research stops.')}</p>
        </>}
        {data.internal&&<>{nodes.map(([key,label])=><label className="radar-field" key={key}><span>{label}</span>
          <select aria-label={label} disabled={busy} value={settings.routes[key]} onChange={e=>setSettings({...settings,routes:{...settings.routes,[key]:e.target.value}})}>
            <option value="api">API billing</option>
            <option value="subscription">My ChatGPT plan</option>
          </select>
        </label>)}
        <p className="radar-muted">The company research choice applies to imported lists. Open search keeps its current API provider.</p>
        <p className="radar-muted">ChatGPT plan: {data.subscription_model}. Uses the dedicated Pi connection. Web search tools retain their own costs. A plan limit pauses work; API billing requires your selection.</p>
        <p><a href="https://chatgpt.com/#settings/Usage" target="_blank" rel="noreferrer">Manage ChatGPT usage</a></p></>}
        <div className="radar-form-actions radar-research-actions">
          <button className="radar-primary-action" disabled={busy||!dirty||(settings.source_mode==='seed_list'&&!selected)} onClick={()=>void save()}>{t('Save research settings')}</button>
          <button className="radar-secondary-action" disabled={busy||dirty} onClick={()=>void perform(async()=>{
            const resumed=await radarApi.resumeResearch(token,workspaceId);
            if(current.current){setNotice(resumed.search_run_ids.length||resumed.batch_ids.length?'Resuming saved work.':'No paused work is eligible to resume.');onChanged?.();}
          })}>{t('Resume paused work')}</button>
        </div>
        {selected&&!dirty&&<>
          <button className="radar-secondary-action" onClick={()=>setCampaignOpen(value=>!value)}>{t(campaignOpen?'Hide list drafts':'Prepare drafts from this list')}</button>
          {open&&campaignOpen&&<RadarListCampaign key={workspaceId+'/'+selected.id} token={token} workspaceId={workspaceId} listId={selected.id} listName={selected.name} onChanged={onChanged}/>}
        </>}
        {!!data.recent_outcomes?.length && <details><summary>{t('Recent company assessments')}</summary>
          <ul>{data.recent_outcomes.slice(0,15).map((item,index)=><li key={item.seed_id+index}>
            <strong>{item.company_name}</strong> · {t(verdicts[item.verdict])}<br/>{item.reason}
          </li>)}</ul>
        </details>}
      </>}
      {notice&&<p role="status">{t(notice)}</p>}
      {error&&<p role="alert">{t(error)}</p>}
    </RadarDialog>
  </>;
}
